export interface Language {
  code: string;
  label: string;
  nativeLabel: string;
  flag: string;
  premium: boolean;
  dir?: "ltr" | "rtl";
  ttsVoices: { id: string; label: string; voiceId: string; emoji: string }[];
}

// Default TTS voice pack — used for every non-Latin/RTL/CJK language.
// Voices are English-primary; the multilingual model handles pronunciation
// for the chosen output language.
const DEFAULT_VOICES = [
  { id: "auto", label: "Tutor (Auto)", voiceId: "FGY2WhTYpPnrIDTdsKH5", emoji: "🎓" },
  { id: "tutor_f", label: "Tutor · Female", voiceId: "EXAVITQu4vr4xnSDxMaL", emoji: "👩‍🏫" },
  { id: "tutor_m", label: "Tutor · Male", voiceId: "onwK4e9ZLuTAKqWW03F9", emoji: "👨‍🏫" },
];

export const LANGUAGES: Language[] = [
  { code: "en", label: "English", nativeLabel: "English", flag: "🇺🇸", premium: false, ttsVoices: [
    ...DEFAULT_VOICES,
    { id: "calm", label: "Calm", voiceId: "pFZP5JQG7iQjIQuC4Bku", emoji: "🧘" },
    { id: "energetic", label: "Energetic", voiceId: "TX3LPaxmHKxFdv7VOQHJ", emoji: "⚡" },
  ]},
  { code: "es", label: "Spanish", nativeLabel: "Español", flag: "🇪🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "fr", label: "French", nativeLabel: "Français", flag: "🇫🇷", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "de", label: "German", nativeLabel: "Deutsch", flag: "🇩🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "pt", label: "Portuguese", nativeLabel: "Português", flag: "🇵🇹", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "it", label: "Italian", nativeLabel: "Italiano", flag: "🇮🇹", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "nl", label: "Dutch", nativeLabel: "Nederlands", flag: "🇳🇱", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "pl", label: "Polish", nativeLabel: "Polski", flag: "🇵🇱", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ru", label: "Russian", nativeLabel: "Русский", flag: "🇷🇺", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "uk", label: "Ukrainian", nativeLabel: "Українська", flag: "🇺🇦", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "zh", label: "Chinese (Simplified)", nativeLabel: "中文（简体）", flag: "🇨🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "zh-TW", label: "Chinese (Traditional)", nativeLabel: "中文（繁體）", flag: "🇹🇼", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ja", label: "Japanese", nativeLabel: "日本語", flag: "🇯🇵", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ko", label: "Korean", nativeLabel: "한국어", flag: "🇰🇷", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "hi", label: "Hindi", nativeLabel: "हिन्दी", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "bn", label: "Bengali", nativeLabel: "বাংলা", flag: "🇧🇩", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ta", label: "Tamil", nativeLabel: "தமிழ்", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "te", label: "Telugu", nativeLabel: "తెలుగు", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "mr", label: "Marathi", nativeLabel: "मराठी", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം", flag: "🇮🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "th", label: "Thai", nativeLabel: "ไทย", flag: "🇹🇭", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "vi", label: "Vietnamese", nativeLabel: "Tiếng Việt", flag: "🇻🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "id", label: "Indonesian", nativeLabel: "Bahasa Indonesia", flag: "🇮🇩", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ms", label: "Malay", nativeLabel: "Bahasa Melayu", flag: "🇲🇾", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "tl", label: "Filipino", nativeLabel: "Filipino", flag: "🇵🇭", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "tr", label: "Turkish", nativeLabel: "Türkçe", flag: "🇹🇷", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "el", label: "Greek", nativeLabel: "Ελληνικά", flag: "🇬🇷", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "cs", label: "Czech", nativeLabel: "Čeština", flag: "🇨🇿", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "sk", label: "Slovak", nativeLabel: "Slovenčina", flag: "🇸🇰", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "hu", label: "Hungarian", nativeLabel: "Magyar", flag: "🇭🇺", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ro", label: "Romanian", nativeLabel: "Română", flag: "🇷🇴", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "bg", label: "Bulgarian", nativeLabel: "Български", flag: "🇧🇬", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "hr", label: "Croatian", nativeLabel: "Hrvatski", flag: "🇭🇷", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "sr", label: "Serbian", nativeLabel: "Српски", flag: "🇷🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "sl", label: "Slovenian", nativeLabel: "Slovenščina", flag: "🇸🇮", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "sv", label: "Swedish", nativeLabel: "Svenska", flag: "🇸🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "no", label: "Norwegian", nativeLabel: "Norsk", flag: "🇳🇴", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "da", label: "Danish", nativeLabel: "Dansk", flag: "🇩🇰", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "fi", label: "Finnish", nativeLabel: "Suomi", flag: "🇫🇮", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "et", label: "Estonian", nativeLabel: "Eesti", flag: "🇪🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "lv", label: "Latvian", nativeLabel: "Latviešu", flag: "🇱🇻", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "lt", label: "Lithuanian", nativeLabel: "Lietuvių", flag: "🇱🇹", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "is", label: "Icelandic", nativeLabel: "Íslenska", flag: "🇮🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ga", label: "Irish", nativeLabel: "Gaeilge", flag: "🇮🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "cy", label: "Welsh", nativeLabel: "Cymraeg", flag: "🏴󠁧󠁢󠁷󠁬󠁳󠁿", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ca", label: "Catalan", nativeLabel: "Català", flag: "🇪🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "eu", label: "Basque", nativeLabel: "Euskara", flag: "🇪🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "gl", label: "Galician", nativeLabel: "Galego", flag: "🇪🇸", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "sw", label: "Swahili", nativeLabel: "Kiswahili", flag: "🇰🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "af", label: "Afrikaans", nativeLabel: "Afrikaans", flag: "🇿🇦", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "am", label: "Amharic", nativeLabel: "አማርኛ", flag: "🇪🇹", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "yo", label: "Yoruba", nativeLabel: "Yorùbá", flag: "🇳🇬", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "zu", label: "Zulu", nativeLabel: "isiZulu", flag: "🇿🇦", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ha", label: "Hausa", nativeLabel: "Hausa", flag: "🇳🇬", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "so", label: "Somali", nativeLabel: "Soomaali", flag: "🇸🇴", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "kk", label: "Kazakh", nativeLabel: "Қазақша", flag: "🇰🇿", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "uz", label: "Uzbek", nativeLabel: "Oʻzbekcha", flag: "🇺🇿", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "az", label: "Azerbaijani", nativeLabel: "Azərbaycanca", flag: "🇦🇿", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ka", label: "Georgian", nativeLabel: "ქართული", flag: "🇬🇪", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "hy", label: "Armenian", nativeLabel: "Հայերեն", flag: "🇦🇲", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "mn", label: "Mongolian", nativeLabel: "Монгол", flag: "🇲🇳", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "my", label: "Burmese", nativeLabel: "မြန်မာ", flag: "🇲🇲", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "km", label: "Khmer", nativeLabel: "ខ្មែរ", flag: "🇰🇭", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "lo", label: "Lao", nativeLabel: "ລາວ", flag: "🇱🇦", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "si", label: "Sinhala", nativeLabel: "සිංහල", flag: "🇱🇰", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "ne", label: "Nepali", nativeLabel: "नेपाली", flag: "🇳🇵", premium: false, ttsVoices: DEFAULT_VOICES },
  { code: "he", label: "Hebrew", nativeLabel: "עברית", flag: "🇮🇱", premium: false, dir: "rtl", ttsVoices: DEFAULT_VOICES },
  { code: "ar", label: "Arabic", nativeLabel: "العربية", flag: "🇸🇦", premium: false, dir: "rtl", ttsVoices: DEFAULT_VOICES },
  { code: "fa", label: "Persian", nativeLabel: "فارسی", flag: "🇮🇷", premium: false, dir: "rtl", ttsVoices: DEFAULT_VOICES },
  { code: "ur", label: "Urdu", nativeLabel: "اردو", flag: "🇵🇰", premium: false, dir: "rtl", ttsVoices: DEFAULT_VOICES },
];

export const DEFAULT_LANGUAGE = LANGUAGES[0];

/**
 * Resolve any BCP-47-ish tag to a supported language.
 * Handles regional variants ("es-MX"), underscores ("pt_BR") and POSIX
 * suffixes ("en-US@posix") that browsers/OS locales can produce.
 */
export function getLanguageByCode(code?: string | null): Language {
  if (!code) return DEFAULT_LANGUAGE;
  const clean = code.replace(/@.*$/, "").replace(/_/g, "-").trim();
  const lower = clean.toLowerCase();

  const exact = LANGUAGES.find((l) => l.code.toLowerCase() === lower);
  if (exact) return exact;

  const [base, region] = lower.split("-");
  if (base === "zh") {
    const traditional = region === "tw" || region === "hk" || region === "mo" || region === "hant";
    return LANGUAGES.find((l) => l.code === (traditional ? "zh-TW" : "zh")) || DEFAULT_LANGUAGE;
  }
  return LANGUAGES.find((l) => l.code.toLowerCase() === base) || DEFAULT_LANGUAGE;
}


export function getAvailableLanguages(isPaid: boolean): Language[] {
  return LANGUAGES.filter((l) => !l.premium || isPaid);
}

export const LANGUAGE_NAMES: Record<string, string> = Object.fromEntries(
  LANGUAGES.map((l) => [l.code, l.label])
);
